import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { formatUnknownError } from '@/lib/error-format';
import { queryKeys } from '@/lib/query-keys';
import { getCapabilitiesForRole, type AppCapability } from '@/lib/capabilities';

// Types
export type OrganizationRole = 'admin' | 'manager' | 'user';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  settings: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface OrganizationMember {
  id: string;
  organization_id: string;
  user_id: string;
  role: OrganizationRole;
  invited_by: string | null;
  joined_at: string;
  // Joined data
  email?: string;
  last_sign_in_at?: string | null;
  profile?: {
    full_name: string | null;
  };
}

export interface OrganizationInvite {
  id: string;
  organization_id: string;
  email: string;
  role: OrganizationRole;
  invited_by: string;
  token: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  expires_at: string;
  created_at: string;
  accepted_at: string | null;
  // Joined data
  inviter?: {
    full_name: string | null;
  };
  organization?: {
    name: string;
  };
}

export interface OrganizationMembership {
  role: OrganizationRole;
  organization: Organization;
}

// Main hook for getting current user's organization
export function useOrganization() {
  const { user } = useAuth();

  const { data: membership, isLoading, error } = useQuery({
    queryKey: queryKeys.organization.membership(user?.id),
    queryFn: async (): Promise<OrganizationMembership | null> => {
      const { data, error } = await supabase
        .from('organization_members')
        .select(`
          role,
          organization:organizations(id, name, slug, settings, created_at, updated_at)
        `)
        .eq('user_id', user!.id)
        .single();

      if (error) {
        // User may not have an org yet - this is OK
        if (error.code === 'PGRST116') {
          return null;
        }
        throw error;
      }

      // Handle the nested select result
      const org = data.organization as unknown as Organization;
      return {
        role: data.role as OrganizationRole,
        organization: org,
      };
    },
    enabled: !!user,
  });

  const capabilities = getCapabilitiesForRole(membership?.role);

  return {
    organization: membership?.organization ?? null,
    role: membership?.role ?? null,
    isLoading,
    error,
    capabilities,
    hasCapability: (capability: AppCapability) => capabilities[capability],
    isAdmin: membership?.role === 'admin',
    isManager: membership?.role === 'manager' || membership?.role === 'admin',
    isUser: membership?.role === 'user',
    hasOrganization: !!membership?.organization,
  };
}

// Hook for organization members management
export function useOrganizationMembers() {
  const { organization } = useOrganization();

  return useQuery({
    queryKey: queryKeys.organization.members(organization?.id),
    queryFn: async (): Promise<OrganizationMember[]> => {
      // First get members
      const { data: members, error } = await supabase
        .from('organization_members')
        .select('*')
        .eq('organization_id', organization!.id)
        .order('joined_at', { ascending: true });

      if (error) throw error;

      // Get user emails and profiles separately
      const userIds = members.map(m => m.user_id);

      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, full_name')
        .in('user_id', userIds);

      // Get emails and last login from auth.users via RPC
      const { data: userDetails } = await supabase
        .rpc('get_organization_member_details', { org_id: organization!.id });

      const profileMap = new Map(profiles?.map(p => [p.user_id, p]) ?? []);
      const userDetailsMap = new Map(
        userDetails?.map((u: { user_id: string; email: string; last_sign_in_at: string | null }) =>
          [u.user_id, u]
        ) ?? []
      );

      return members.map(m => ({
        ...m,
        role: m.role as OrganizationRole,
        profile: profileMap.get(m.user_id) ?? null,
        email: userDetailsMap.get(m.user_id)?.email,
        last_sign_in_at: userDetailsMap.get(m.user_id)?.last_sign_in_at,
      }));
    },
    enabled: !!organization?.id,
  });
}

// Hook for updating a member's role
export function useUpdateMemberRole() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { organization } = useOrganization();

  return useMutation({
    mutationFn: async ({ memberId, newRole }: { memberId: string; newRole: OrganizationRole }) => {
      const { error } = await supabase
        .from('organization_members')
        .update({ role: newRole })
        .eq('id', memberId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organization.members(organization?.id) });
      toast({ title: 'Member role updated' });
    },
    onError: (error) => {
      toast({
        title: 'Failed to update role',
        description: formatUnknownError(error),
        variant: 'destructive',
      });
    },
  });
}

// Hook for removing a member
export function useRemoveMember() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { organization } = useOrganization();

  return useMutation({
    mutationFn: async (memberId: string) => {
      const { error } = await supabase
        .from('organization_members')
        .delete()
        .eq('id', memberId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organization.members(organization?.id) });
      toast({ title: 'Member removed' });
    },
    onError: (error) => {
      toast({
        title: 'Failed to remove member',
        description: formatUnknownError(error),
        variant: 'destructive',
      });
    },
  });
}

// Hook for organization invites
export function useOrganizationInvites() {
  const { organization } = useOrganization();

  return useQuery({
    queryKey: queryKeys.organization.invites(organization?.id),
    queryFn: async (): Promise<OrganizationInvite[]> => {
      // First get the invites
      const { data: invites, error } = await supabase
        .from('organization_invites')
        .select('*')
        .eq('organization_id', organization!.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (!invites || invites.length === 0) return [];

      // Get inviter profiles separately
      const inviterIds = [...new Set(invites.map(i => i.invited_by))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, full_name')
        .in('user_id', inviterIds);

      const profileMap = new Map(profiles?.map(p => [p.user_id, p]) ?? []);

      return invites.map(invite => ({
        ...invite,
        role: invite.role as OrganizationRole,
        status: invite.status as OrganizationInvite['status'],
        inviter: profileMap.get(invite.invited_by) ?? null,
      }));
    },
    enabled: !!organization?.id,
  });
}

// Hook for creating an invite
export function useCreateInvite() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { organization } = useOrganization();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ email, role }: { email: string; role: OrganizationRole }) => {
      // Check if invite already exists
      const { data: existing } = await supabase
        .from('organization_invites')
        .select('id')
        .eq('organization_id', organization!.id)
        .eq('email', email.toLowerCase())
        .eq('status', 'pending')
        .single();

      if (existing) {
        throw new Error('An invite for this email already exists');
      }

      // Check if user is already a member
      const { data: existingMember } = await supabase
        .from('organization_members')
        .select('id, user_id')
        .eq('organization_id', organization!.id);

      // Check against profiles for email
      if (existingMember && existingMember.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id')
          .in('user_id', existingMember.map(m => m.user_id));

        // This is a simplified check - in production you'd check auth.users email
      }

      const { data, error } = await supabase
        .from('organization_invites')
        .insert({
          organization_id: organization!.id,
          email: email.toLowerCase(),
          role,
          invited_by: user!.id,
        })
        .select()
        .single();

      if (error) throw error;

      // Get inviter's name for the email
      const { data: inviterProfile } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('user_id', user!.id)
        .single();

      const inviterName = inviterProfile?.full_name || user!.email?.split('@')[0] || 'A team member';

      // Send invite email via edge function
      try {
        const { error: emailError } = await supabase.functions.invoke('send-invite-email', {
          body: {
            inviteId: data.id,
            inviteToken: data.token,
            recipientEmail: data.email,
            recipientRole: data.role,
            organizationName: organization!.name,
            inviterName,
            appUrl: import.meta.env.VITE_APP_URL || window.location.origin,
          },
        });

        if (emailError) {
          console.error('Failed to send invite email:', emailError);
          // Don't throw - invite was created successfully, email just failed
        }
      } catch (emailErr) {
        console.error('Failed to send invite email:', emailErr);
        // Don't throw - invite was created successfully, email just failed
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organization.invites(organization?.id) });
      toast({
        title: 'Invite sent',
        description: `Invitation sent to ${data.email}`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to send invite',
        description: formatUnknownError(error),
        variant: 'destructive',
      });
    },
  });
}

// Hook for revoking an invite
export function useRevokeInvite() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { organization } = useOrganization();

  return useMutation({
    mutationFn: async (inviteId: string) => {
      const { error } = await supabase
        .from('organization_invites')
        .update({ status: 'revoked' })
        .eq('id', inviteId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organization.invites(organization?.id) });
      toast({ title: 'Invite revoked' });
    },
    onError: (error) => {
      toast({
        title: 'Failed to revoke invite',
        description: formatUnknownError(error),
        variant: 'destructive',
      });
    },
  });
}

// Hook for getting invite by token (for accept page)
export function useInviteByToken(token: string | undefined) {
  return useQuery({
    queryKey: queryKeys.organization.inviteByToken(token),
    queryFn: async (): Promise<OrganizationInvite | null> => {
      const { data, error } = await supabase
        .from('organization_invites')
        .select(`
          *,
          organization:organizations(name)
        `)
        .eq('token', token!)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null;
        throw error;
      }

      return {
        ...data,
        role: data.role as OrganizationRole,
        status: data.status as OrganizationInvite['status'],
        organization: data.organization as { name: string } | undefined,
      };
    },
    enabled: !!token,
  });
}

// Hook for accepting an invite
export function useAcceptInvite() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (token: string) => {
      // Use the secure database function to accept the invite
      // This bypasses RLS since the user isn't a member yet
      const { data, error } = await supabase
        .rpc('accept_organization_invite', { invite_token: token });

      if (error) {
        throw new Error(error.message);
      }

      if (!data.success) {
        throw new Error(data.error || 'Failed to accept invite');
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organization.membership() });
      toast({
        title: 'Welcome to the team!',
        description: 'You have successfully joined the organization',
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to accept invite',
        description: formatUnknownError(error),
        variant: 'destructive',
      });
    },
  });
}

// Hook for updating organization settings
export function useUpdateOrganization() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { organization } = useOrganization();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (updates: Partial<Pick<Organization, 'name' | 'settings'>>) => {
      const { error } = await supabase
        .from('organizations')
        .update(updates)
        .eq('id', organization!.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organization.membership(user?.id) });
      toast({ title: 'Organization updated' });
    },
    onError: (error) => {
      toast({
        title: 'Failed to update organization',
        description: formatUnknownError(error),
        variant: 'destructive',
      });
    },
  });
}
